import React from 'react';
import I18n from 'i18n-js';
import { Location } from 'history';
import {
  keyBy,
} from 'lodash';
import {
  CoreCspLayoutQuery,
  core_apimessages_ProductSpec_Type,
} from '../../../schemas/rql';
import MarketplaceFilterRow, { CSP2FiltersOption } from '@reverbdotcom/commons/src/components/marketplace_filter_row';
import { SortChip } from '@reverbdotcom/commons/src/components/rql_search_overview';
import { WidgetType } from '@reverbdotcom/commons/src/components/search_filters';
import { type LayoutQueryResult, type FiltersAndListingsQueryResult } from '../csp_layout';
import { listingsFilters } from '../../listings_filters';
import ListViewToggle from '@reverbdotcom/commons/src/components/list_view_toggle';

const APPROVED_FRETBOARD_FILTER_CATEGORIES = [
  'dfd39027-d134-4353-b9e4-57dc6be791b9', // electric guitars
  '3ca3eb03-7eac-477d-b253-15ce603d2550', // acoustic guitars
  '53a9c7d7-d73d-4e7f-905c-553503e50a90', // bass guitars
];

const FRETBOARD_MATERIAL_TRAIT_UUID = '6a24e2dc-800d-4f06-ac67-acef9569a40f';

interface ICSPFiltersOption {
  name: string;
  total: number;
  selected: boolean;
  optionValue?: string;
  setValues?: any;
  unsetValues?: any;
  paramName?: string;
}

interface IProps {
  location: Location;
  csp: CoreCspLayoutQuery.Csp;
  layoutQueryResult: LayoutQueryResult;
  filtersAndListingsQueryResult: FiltersAndListingsQueryResult;
  queryDataLoading: boolean;
  setListViewState: (val: boolean) => void;
}

export function CSPFilterRow({
  location,
  csp,
  filtersAndListingsQueryResult,
  layoutQueryResult,
  queryDataLoading,
  setListViewState,
}: IProps ) {
  const {
    canonicalFinish = '',
    traitValues: fretboardMaterial = '',
  } = location?.query ?? {};
  const filtersAndListingsLoading = filtersAndListingsQueryResult.loading;
  const filtersAndListingsData = filtersAndListingsQueryResult.data ?? filtersAndListingsQueryResult.previousData;
  const layoutData = layoutQueryResult.data ?? layoutQueryResult.previousData;

  const filtersFromSearch = filtersAndListingsData?.allListings?.filters || [];
  const filters = listingsFilters(filtersFromSearch, location);

  function shouldRenderFretboardMaterialFilters() {
    return APPROVED_FRETBOARD_FILTER_CATEGORIES.includes(csp?.categories[0]?.parentCategories[0]?.id);
  }

  function isFinishFilterSelected(finish) {
    return canonicalFinish === finish;
  }

  function isFretboardMaterialSelected(material) {
    return fretboardMaterial === material;
  }

  function getFinishFilters(): ICSPFiltersOption[] {
    const finishAgg = filtersAndListingsData?.finishAgg?.aggregationResults[0]?.aggregationDetails || [];
    const totalByFinish = keyBy(finishAgg, 'key');
    const total = finish => (totalByFinish[finish]?.count || 0);

    const finishProductSpecs = (layoutData?.csp?.specs?.productSpecs || []).filter(
      spec => spec.type === core_apimessages_ProductSpec_Type.FINISH,
    );

    const options = finishProductSpecs
      .map((spec) => {
        return {
          name: spec.value,
          selected: isFinishFilterSelected(spec.value),
          total: total(spec.value),
          optionValue: spec.value,
          setValues: isFinishFilterSelected(spec.value) ? [] : [spec.value],
          unsetValues: isFinishFilterSelected(spec.value) ? [spec.value] : [],
          paramName: 'canonicalFinish',
        };
      });

    return options;
  }

  function getFretboardMaterialFilters(): CSP2FiltersOption[] {
    if (!shouldRenderFretboardMaterialFilters()) return [];

    const fretboardMaterialAgg = filtersAndListingsData?.fretboardMaterialAgg?.aggregationResults[0]?.aggregationDetails || [];
    const totalByMaterial = keyBy(fretboardMaterialAgg, 'displayName');
    const total = material => (totalByMaterial[material]?.count || 0);

    const options = fretboardMaterialAgg
      .map(spec => {
        return {
          name: spec.displayName,
          selected: isFretboardMaterialSelected(spec.key),
          total: total(spec.displayName),
          optionValue: spec.key,
          setValues: isFretboardMaterialSelected(spec.key) ? [] : [spec.key],
          unsetValues: isFretboardMaterialSelected(spec.key) ? [spec.key] : [],
          paramName: 'traitValues',
        };
      });

    return options;
  }

  // remove fretboard material from the full electric guitar trait facets list so we don't have a duplicate filter
  const filteredFilters = filters.filter(filter => filter.key !== FRETBOARD_MATERIAL_TRAIT_UUID );

  return (
    <div className="csp2-marketplace-filters">
      <div className="search-overview__primary">
        <div className="search-overview__count">
          <h3>{I18n.t('discovery.csp2.compareListings')}</h3>
        </div>
        {!queryDataLoading && (
          <div className="d-inline">
            <ListViewToggle
              setListViewState={setListViewState}
              loading={filtersAndListingsLoading}
              id="csp-toggle"
            />
          </div>
        )}
        <div className="search-overview__feed">
          <SortChip
            loading={filtersAndListingsLoading}
            sort={filters.find(filter => filter.widgetType === WidgetType.SORT)}
            trackingQuery=""
          />
        </div>
      </div>
      <div className="search-overview__secondary">
        <MarketplaceFilterRow
          filters={filteredFilters}
          loading={filtersAndListingsLoading}
          location={location}
          finishOptions={getFinishFilters()}
          fretboardMaterialOptions={getFretboardMaterialFilters()}
          hideOnMobile
          dynamicTitle
          displayCount
          showTraitFilters
        />
      </div>
    </div>
  );
}
